import {
  LogItem,
  IPlugin,
  userTypeMap,
  accountTypeMap,
  queryToObj,
  uuid,
  checkWin,
  utils,
} from '@alife/cone-arms-core';
import TracePvPlugin from '@alife/cone-arms-plugin-pv';
import TraceWebPerformancePlugin from '@alife/cone-arms-plugin-web-performance';
import TraceApiPlugin from '@alife/cone-arms-plugin-api';
import TraceResourceErrorPlugin from '@ali/trace-plugin-resource-error';
import TraceBlankScreenPlugin from '@alife/cone-arms-plugin-blank-screen';
import TraceWebRuntimePlugin from '@alife/cone-arms-plugin-web-runtime';
import TraceCNUIEnhancePlugin from '@alife/cone-arms-plugin-cnui-auto-enhance';
import TraceTranslationCoveragePlugin from './translation-coverage-plugin';
import traceSdk from './sdk';
import {
  validatePid,
  getVersion,
  genApiPluginOptions,
  inWindow,
  parseReportPayload,
  getPageDevType,
  getSpmAB,
  isDebug,
  getCNUIVersion,
  getClientInfo,
  getUidFromCookieWithAccountType,
  getEmployeeNumberFromCookie,
  getSessionIdFromStorage,
  compareVersion,
  getEnv,
} from './utils';
import { InitTraceOptions, ITrace } from './types';
import TraceUxPlugin from './ux-plugin';

import TraceMCPPlugin from './mcp-plugin';
import { extendTraceInstance } from './extend-trace';
import traceInstance from './global-trace';
import defaultSetting from './setting.json';
// import { getWorkNo } from './fetch-workno';
import { fixIosPerformanceObserveBug } from './performance-observe-ios-fix';
import { startCatchPreError, doErrorReportAndStopWatchPreError } from './catch-pre-error';

const { sdkVersion } = defaultSetting;

const reportHistory = [] as LogItem[];

let sdkCdnTag = '';

/**
 * 添加coneArms默认上报参数
 * @param payload
 * @returns
 */
function addConeArmsDefaultReportParams(payload: LogItem) {
  const needAddCNParamsReportTypes = [
    'pv',
    'api',
    'perf',
    'event',
    'error',
    'resource_error',
    'action',
    'runtime',
    'performance',
  ];
  const extParams: Partial<LogItem> = getClientInfo();
  if (payload && needAddCNParamsReportTypes.indexOf(payload.type) > -1) {
    // 上报页面开发类型
    if (typeof payload.p25 === 'undefined') {
      extParams.p25 = getPageDevType();
    }
  }
  const language = navigator.language || undefined;
  if (payload && payload.type === 'pv') {
    // 上报浏览器语言和时区设置
    if (typeof payload.p1 === 'undefined' && language) {
      extParams.p1 = language;
    }
    const dateIns = new Date();
    if (typeof payload.p2 === 'undefined' && typeof dateIns.getTimezoneOffset === 'function') {
      const hourOffset = 0 - Math.ceil(dateIns.getTimezoneOffset() / 60);
      extParams.p2 = `UTC${hourOffset > 0 ? '+' : ''}${hourOffset}`;
    }
    // 上报cnui版本号信息
    if (typeof payload.p3 === 'undefined') {
      extParams.p3 = JSON.stringify(getCNUIVersion());
    }
  }
  const needAppendReportParams: any = {};
  Object.keys(extParams).forEach((key) => {
    if (typeof payload[key] === 'undefined') {
      needAppendReportParams[key] = extParams[key];
    }
  });
  Object.assign(payload, needAppendReportParams);
  if (payload && sdkCdnTag) {
    const clientExtraInfo = payload.client_extra_info || {};
    clientExtraInfo.sdk_cdn_tag = sdkCdnTag;
    Object.assign(payload, { client_extra_info: clientExtraInfo });
  }
  return payload;
}

function beforeSendHook(payload: LogItem) {
  const retItem = addConeArmsDefaultReportParams(payload);
  if (isDebug()) {
    reportHistory.push(payload);
  }
  return retItem;
}

function initTraceInvoke(options: InitTraceOptions): ITrace {
  const needMergeGlobalOption =
    options && !(options.mountToWindow === false || options.disableAllDefaultPlugins === true);
  const finalOptions = needMergeGlobalOption
    ? Object.assign({}, window.cnArmsPresetInitOptions, options)
    : options;
  const {
    pid,
    accountType,
    uid,
    employeeNumber,
    checkPid = true,
    mountToWindow = true,
    pvPluginOptions = {},
    perfPluginOptions = {},
    apiPluginOptions = {},
    uxPluginOptions = {},
    mcpPluginOptions = {},
    blankScreenPluginOptions = {},
    cnuiEnhancePluginOptions = {},
    morePlugins = [],
    defaultPluginSwitch = {},
    spaRouteType: routeType,
    disableAllDefaultPlugins = false,
    errorEnable = true,
    ...traceOptions
  } = finalOptions;
  let spaRouteType: string = (routeType as string) || '';
  if (['history', 'hash'].indexOf(spaRouteType) === -1 && pvPluginOptions) {
    if (pvPluginOptions.enableHash) {
      spaRouteType = 'hash';
    } else if (pvPluginOptions.enableHistory) {
      spaRouteType = 'history';
    }
  }

  validatePid({ pid, checkPid });
  const defaultPlugins = [];
  const isPluginDisabled = (pluginName: string) => {
    const switchKey = `disabled${pluginName}Plugin`;
    return (
      disableAllDefaultPlugins === true ||
      (defaultPluginSwitch &&
        switchKey in defaultPluginSwitch &&
        // @ts-ignore type fix
        defaultPluginSwitch[switchKey] === true)
    );
  };
  if (isPluginDisabled('Pv') !== true) {
    defaultPlugins.push([TracePvPlugin, { autoPV: false, ...pvPluginOptions }]);
  }
  if (isPluginDisabled('Perf') !== true) {
    defaultPlugins.push([
      TraceWebPerformancePlugin,
      {
        routeType: spaRouteType,
        ...perfPluginOptions,
      },
    ]);
  }
  if (isPluginDisabled('Api') !== true) {
    defaultPlugins.push([TraceApiPlugin, genApiPluginOptions(apiPluginOptions)]);
  }
  if (isPluginDisabled('ResourceError') !== true) {
    defaultPlugins.push([TraceResourceErrorPlugin]);
  }
  if (isPluginDisabled('Ux') !== true) {
    defaultPlugins.push([TraceUxPlugin, uxPluginOptions]);
  }

  if (isPluginDisabled('MCP') !== true && mcpPluginOptions && 'auto' in mcpPluginOptions) {
    defaultPlugins.push([TraceMCPPlugin, { auto: true, ...mcpPluginOptions }]);
  }

  if (isPluginDisabled('BlankScreen') !== true) {
    defaultPlugins.push([
      TraceBlankScreenPlugin,
      Object.assign(
        {
          detectedDom: ['#root', '#spa-root'],
          isPolling: true,
        },
        blankScreenPluginOptions,
      ),
    ]);
  }

  if (isPluginDisabled('WebRuntime') !== true) {
    defaultPlugins.push([TraceWebRuntimePlugin]);
  }

  if (isPluginDisabled('CNUIEnhance') !== true) {
    defaultPlugins.push([TraceCNUIEnhancePlugin, cnuiEnhancePluginOptions]);
  }

  if (isPluginDisabled('TranslationCoverage') !== true && !window?.__PANDA?.V3Monitor) {
    defaultPlugins.push([TraceTranslationCoveragePlugin, sdkCdnTag]);
  }

  let plugins;
  if (morePlugins.length > 0 || defaultPlugins.length > 0) {
    plugins = [...defaultPlugins, ...morePlugins] as IPlugin[];
  }

  const version = getVersion();

  // 修复ios上PerformanceObserve的bug
  fixIosPerformanceObserveBug();

  const originTrace = traceSdk<ITrace>({
    pid, // 必填 平台上申请的的pid
    env: getEnv(), // 可选 prod | pre | daily | string
    version,
    // aplusUrl: 'gm.mmstat.com', // 可选 默认gm.mmstat.com, 海外埋点配置 sg.mmstat.com
    plugins,
    // @ts-ignore type fix
    spaRouteType,
    accountType,
    uid: getUidFromCookieWithAccountType(accountType) || uid,
    employeeNumber: getEmployeeNumberFromCookie(accountType) || employeeNumber,
    errorEnable: errorEnable && disableAllDefaultPlugins !== true,
    sid: getSessionIdFromStorage(uuid()),
    ...traceOptions,
  });

  const newTrace = extendTraceInstance(originTrace);

  if (newTrace.logApi) {
    const originLogApi = newTrace.logApi.bind(newTrace);

    newTrace.logApi = (logItem: any = {}) => {
      const { _originRequestIns, ...realLogItem } = logItem;
      if (_originRequestIns) {
        setTimeout(() => {
          originLogApi({
            ...realLogItem,
            errorMsgUseCount: _originRequestIns.__errorMessageReadCount || 0,
          });
        }, 100);
      } else {
        originLogApi(realLogItem);
      }
    };
  }

  // 注册全局hooks，并启动 trace 并监听全局 JS 异常
  if (newTrace._hasInstalledPlugin !== true) {
    newTrace.install();
    newTrace.before('send', (payload: LogItem) => {
      const newPayload = beforeSendHook(payload);
      const spm = getSpmAB(newTrace.getConfig());
      return {
        ...newPayload,
        spm_a: newPayload.spm_a || spm.spm_a,
        spm_b: newPayload.spm_b || spm.spm_b,
      };
    });
    newTrace._hasInstalledPlugin = true;
  }

  // 上报初始化之前发生的错误
  if (
    disableAllDefaultPlugins !== true &&
    (newTrace.getConfig().errorEnable || !isPluginDisabled('ResourceError'))
  ) {
    doErrorReportAndStopWatchPreError(newTrace);
  }

  // mount to window
  if (mountToWindow !== false && inWindow() && !window.coneArmsTrace) {
    window.trace = newTrace;
    window.coneArmsTrace = newTrace;
    window.parseArmsReportPayload = parseReportPayload;
    window.getConeArmsReportHistory = () => reportHistory;
  }
  if (inWindow() && location.search.slice(1).indexOf('arms_biz_flow_info') > -1) {
    const bizFlowInfoStr = queryToObj(location.search.slice(1)).arms_biz_flow_info;
    try {
      const bizFlowInfo = JSON.parse(decodeURIComponent(bizFlowInfoStr));
      if (bizFlowInfo && Object.keys(bizFlowInfo).length > 0) {
        newTrace.setBizFlowInfo(bizFlowInfo);
      }
    } catch (err) {
      // console.error('bizFlowInfo parse error', err);
    }
  }

  return newTrace;
}

function getConeArmsSpaAutoPv(): () => null {
  throw new Error('getConeArmsSpaAutoPv, 已废弃，直接配置 pvPluginOptions.autoPV=true 即可');
}

function setSdkCdnTag(tag: string) {
  sdkCdnTag = tag;
}

const initTrace = (options: InitTraceOptions) => {
  if (
    checkWin() &&
    window.coneArms &&
    typeof window.coneArms.initTrace === 'function' &&
    window.coneArms.initTrace !== initTrace &&
    compareVersion(window.coneArms.sdkVersion, sdkVersion) > -1
  ) {
    // 优先使用全局加载的较新版本的sdk
    return window.coneArms.initTrace(options);
  }
  return initTraceInvoke(options);
};

const { cleanUrl } = utils;

export {
  initTrace,
  getConeArmsSpaAutoPv,
  TraceUxPlugin,
  traceInstance,
  userTypeMap,
  sdkVersion,
  accountTypeMap,
  traceSdk,
  setSdkCdnTag,
  startCatchPreError,
  cleanUrl,
};

export type { ITrace, InitTraceOptions };
